import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, ArrowClockwise, Trash, DownloadSimple, CheckCircle, WarningCircle, Prohibit, CircleNotch } from "@phosphor-icons/react";

const VERDICT = {
  inbox: { label: "Inbox", cls: "bg-[#0e2a16] text-[#5fd08a] border-[#1f5a35]", icon: CheckCircle },
  spam: { label: "Spam", cls: "bg-[#2a0e0e] text-[#ff6b6b] border-[#5a1f1f]", icon: Prohibit },
  undelivered: { label: "Not delivered", cls: "bg-[#231f0e] text-[#F4C430] border-[#5a4e1f]", icon: WarningCircle },
  error: { label: "Error", cls: "bg-[#1a1a1a] text-[#9aa0a6] border-[#2A2A2A]", icon: WarningCircle },
};

const PHASE_LABEL = {
  sending: "Sending emails",
  reading: "Waiting for delivery",
  grace_sweep: "Final grace sweep",
  done: "Done",
  interrupted: "Interrupted",
  error: "Error",
};

function statusLine(t) {
  if (t.status === "done") return "done";
  if (t.status === "interrupted") return "interrupted — run again";
  if (t.status === "error") return "error";
  const ph = PHASE_LABEL[t.phase] || "running";
  if (t.status === "grace_sweep") return ph;
  if (t.phase === "sending" && t.round_total)
    return `round ${t.current_round}/${t.max_tries} · sending ${t.sent_count || 0}/${t.round_total}`;
  return t.current_round ? `round ${t.current_round}/${t.max_tries} · ${ph}` : ph;
}

export default function MailboxTest() {
  const [senders, setSenders] = useState([]);
  const [selected, setSelected] = useState({});
  const [paste, setPaste] = useState("");
  const [provider, setProvider] = useState("google");
  const [maxTries, setMaxTries] = useState(3);
  const [tests, setTests] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phaseFilter, setPhaseFilter] = useState("all");

  const loadSenders = useCallback(async () => {
    try {
      const { data } = await api.get(`/senders?provider=${provider}&limit=500`);
      setSenders(data.senders || []);
    } catch (_e) { /* ignore */ }
  }, [provider]);

  const loadTests = useCallback(async () => {
    try {
      const { data } = await api.get("/mailbox-test");
      setTests(data.tests || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { loadSenders(); }, [loadSenders]);
  useEffect(() => { loadTests(); const iv = setInterval(loadTests, 5000); return () => clearInterval(iv); }, [loadTests]);

  const loadDetail = useCallback(async (id) => {
    try { const { data } = await api.get(`/mailbox-test/${id}`); setDetail(data); } catch (_e) { /* ignore */ }
  }, []);

  const downloadCsv = useCallback(async (id) => {
    try {
      const r = await api.get(`/mailbox-test/${id}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `mailbox-test-${id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    loadDetail(active);
    const iv = setInterval(() => loadDetail(active), 5000);
    return () => clearInterval(iv);
  }, [active, loadDetail]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const results = detail?.results || [];
  const maxPhases = detail?.max_tries || 3;
  const phaseCounts = {};
  for (let p = 1; p <= maxPhases; p++) phaseCounts[p] = results.filter((r) => r.tries_used === p).length;
  const filteredResults = phaseFilter === "all" ? results : results.filter((r) => r.tries_used === Number(phaseFilter));
  const isRunning = detail && !["done", "error", "interrupted"].includes(detail.status);
  const isSending = detail && detail.phase === "sending";
  const pct = detail && detail.total_senders ? Math.round((detail.completed_senders / detail.total_senders) * 100) : 0;
  const sendPct = detail && detail.round_total ? Math.round((detail.sent_count / detail.round_total) * 100) : 0;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectAll = () => { const n = {}; senders.forEach((s) => { n[s.id] = true; }); setSelected(n); };
  const clearAll = () => setSelected({});

  const applyPaste = async () => {
    const emails = paste.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter((e) => e.includes("@"));
    if (!emails.length) { toast.error("Paste some email addresses first"); return; }
    try {
      const { data } = await api.post("/senders/lookup", { emails });
      const next = { ...selected };
      (data.matched || []).forEach((m) => { next[m.id] = true; });
      setSelected(next);
      toast.success(`Matched ${(data.matched || []).length} mailbox(es)${data.missed?.length ? `, ${data.missed.length} not found` : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const runTest = async () => {
    if (selectedIds.length < 2) { toast.error("Select at least 2 mailboxes"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/mailbox-test", { sender_ids: selectedIds, max_tries: Number(maxTries) });
      toast.success(`Test started on ${data.total_senders} mailbox(es)${data.skipped_emails?.length ? ` (${data.skipped_emails.length} skipped — not IMAP-testable)` : ""}`);
      setActive(data.id);
      loadTests();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/mailbox-test/${id}`); if (active === id) { setActive(null); setDetail(null); } loadTests(); }
    catch (_e) { /* ignore */ }
  };

  return (
    <div className="p-6 max-w-[1200px]" data-testid="mailbox-test-page">
      <div className="flex items-center gap-3 mb-1">
        <MagnifyingGlass size={22} weight="bold" className="text-[#E1FF00]" />
        <h1 className="font-heading text-2xl font-bold tracking-tight">Mailbox Test</h1>
      </div>
      <p className="text-sm text-[#A1A1AA] mb-6 max-w-[760px]">
        Your mailboxes send to each other and we read the receiving inbox over IMAP to see if each one lands in <b className="text-[#5fd08a]">Inbox</b> or <b className="text-[#ff6b6b]">Spam</b>.
        Each sender is tried up to {maxTries}× (same mailbox twice, then a different one) before being confirmed as Spam — so you can spot and pause mailboxes that aren't inboxing.
        <span className="block mt-1 text-[#71717A]">Needs app-password mailboxes (Gmail/Outlook) with IMAP enabled + a proxy. Test emails use pool content with random details and no link.</span>
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
        {/* Left: build a test */}
        <div className="border border-[#2A2A2A] bg-[#0D0D0D] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-[#A1A1AA]">Pick mailboxes</h2>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}
              data-testid="mailbox-provider-select"
              className="bg-[#0A0A0A] border border-[#2A2A2A] text-xs px-2 py-1 text-white">
              <option value="google">Google</option>
              <option value="microsoft">Microsoft</option>
            </select>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <button onClick={selectAll} data-testid="select-all-btn" className="text-xs px-2 py-1 border border-[#2A2A2A] text-[#A1A1AA] hover:text-white">Select all</button>
            <button onClick={clearAll} data-testid="clear-all-btn" className="text-xs px-2 py-1 border border-[#2A2A2A] text-[#A1A1AA] hover:text-white">Clear</button>
            <span className="text-xs text-[#71717A] ml-auto">{selectedIds.length} selected</span>
          </div>

          <div className="max-h-[260px] overflow-y-auto border border-[#1f1f1f] divide-y divide-[#161616]" data-testid="mailbox-list">
            {senders.length === 0 && <div className="text-xs text-[#71717A] p-3">No {provider} senders found.</div>}
            {senders.map((s) => (
              <label key={s.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#0F0F0F]" data-testid={`mailbox-row-${s.id}`}>
                <input type="checkbox" checked={!!selected[s.id]} onChange={() => toggle(s.id)} data-testid={`mailbox-check-${s.id}`} />
                <span className="font-mono text-[12px] truncate">{s.email}</span>
              </label>
            ))}
          </div>

          <div className="mt-3">
            <div className="text-xs text-[#A1A1AA] mb-1">Or bulk-paste emails</div>
            <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={3}
              data-testid="bulk-paste-input"
              placeholder="rohit@gmail.com, rahul@gmail.com ..."
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] text-xs p-2 font-mono text-white" />
            <button onClick={applyPaste} data-testid="apply-paste-btn" className="mt-1 text-xs px-3 py-1 border border-[#2A2A2A] text-[#A1A1AA] hover:text-white">Match &amp; select</button>
          </div>

          <div className="mt-4 flex items-center gap-3">
            <label className="text-xs text-[#A1A1AA] flex items-center gap-2">
              Tries before confirming Spam
              <select value={maxTries} onChange={(e) => setMaxTries(e.target.value)} data-testid="max-tries-select"
                className="bg-[#0A0A0A] border border-[#2A2A2A] text-xs px-2 py-1 text-white">
                <option value={1}>1</option><option value={3}>3</option><option value={4}>4</option>
              </select>
            </label>
            <button onClick={runTest} disabled={loading || selectedIds.length < 2}
              data-testid="run-test-btn"
              className="ml-auto px-4 py-2 text-sm font-bold bg-[#E1FF00] text-black hover:bg-[#c9e600] disabled:opacity-40">
              {loading ? "Starting..." : "Run test"}
            </button>
          </div>
        </div>

        {/* Right: tests list */}
        <div className="border border-[#2A2A2A] bg-[#0D0D0D] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-[#A1A1AA]">Tests</h2>
            <button onClick={loadTests} data-testid="refresh-tests-btn" className="text-[#A1A1AA] hover:text-white"><ArrowClockwise size={16} weight="bold" /></button>
          </div>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {tests.length === 0 && <div className="text-xs text-[#71717A]">No tests yet.</div>}
            {tests.map((t) => (
              <div key={t.id} onClick={() => setActive(t.id)}
                data-testid={`test-row-${t.id}`}
                className={`flex items-center gap-3 px-3 py-2 border cursor-pointer ${active === t.id ? "border-[#E1FF00] bg-[#0F0F0F]" : "border-[#1f1f1f] hover:bg-[#0F0F0F]"}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.name}</div>
                  <div className="text-[11px] text-[#71717A] font-mono">{t.completed_senders}/{t.total_senders} done · {statusLine(t)}</div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); del(t.id); }} data-testid={`delete-test-${t.id}`} className="text-[#71717A] hover:text-[#ff6b6b]"><Trash size={14} /></button>
              </div>
            ))}
          </div>

          {detail && (
            <div className="mt-4 border-t border-[#1f1f1f] pt-3" data-testid="test-detail">
              {/* live progress meter */}
              {isRunning && (
                <div className="mb-3 border border-[#2A2A2A] bg-[#0A0A0A] p-3" data-testid="test-progress">
                  <div className="flex items-center gap-2 text-[12px] mb-2">
                    <CircleNotch size={14} weight="bold" className="text-[#E1FF00] animate-spin" />
                    <span className="font-mono text-[#E1FF00]">{statusLine(detail)}</span>
                    <span className="ml-auto font-mono text-[#71717A]">
                      {isSending
                        ? `${detail.sent_count || 0}/${detail.round_total || 0} sent`
                        : `${detail.completed_senders}/${detail.total_senders} resolved`}
                    </span>
                  </div>
                  <div className="h-1.5 w-full bg-[#1a1a1a] overflow-hidden" data-testid="progress-bar">
                    <div className="h-full bg-[#E1FF00] transition-all duration-500" style={{ width: `${isSending ? sendPct : pct}%` }} />
                  </div>
                  <div className="mt-2 text-[11px] text-[#71717A] font-mono">
                    {isSending
                      ? `Round ${detail.current_round}/${detail.max_tries} — sending ${detail.round_total || 0} test emails, then reading each inbox over IMAP.`
                      : `${detail.active_count || 0} still testing · emails that already arrived are classified instantly; only the ones not yet received are re-checked.`}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono">
                  <Badge v="inbox" n={detail.counts?.inbox || 0} />
                  <Badge v="spam" n={detail.counts?.spam || 0} />
                  <Badge v="undelivered" n={detail.counts?.undelivered || 0} />
                  <Badge v="error" n={detail.counts?.error || 0} />
                </div>
                <button onClick={() => downloadCsv(detail.id)} data-testid="export-csv-link"
                  className="flex items-center gap-1 text-xs text-[#A1A1AA] hover:text-white">
                  <DownloadSimple size={14} /> CSV
                </button>
              </div>
              {detail.skipped_emails?.length > 0 && (
                <div className="text-[11px] text-[#F4C430] mb-2">Skipped (not IMAP-testable): {detail.skipped_emails.join(", ")}</div>
              )}

              {/* phase meter + filter */}
              {results.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap mb-2" data-testid="phase-filter">
                  <button onClick={() => setPhaseFilter("all")} data-testid="phase-filter-all"
                    className={`text-[11px] font-mono px-2 py-0.5 border ${phaseFilter === "all" ? "border-[#E1FF00] text-[#E1FF00]" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"}`}>
                    All {results.length}
                  </button>
                  {Array.from({ length: maxPhases }, (_, i) => i + 1).map((p) => (
                    <button key={p} onClick={() => setPhaseFilter(String(p))} data-testid={`phase-filter-${p}`}
                      className={`text-[11px] font-mono px-2 py-0.5 border ${phaseFilter === String(p) ? "border-[#E1FF00] text-[#E1FF00]" : "border-[#2A2A2A] text-[#8E8E93] hover:text-white"}`}
                      title={`Mailboxes resolved after ${p} tr${p === 1 ? "y" : "ies"}`}>
                      Phase {p} · {phaseCounts[p] || 0}
                    </button>
                  ))}
                </div>
              )}

              <div className="divide-y divide-[#161616] border border-[#1f1f1f] max-h-[360px] overflow-y-auto">
                {filteredResults.map((r) => {
                  const v = VERDICT[r.verdict] || VERDICT.error;
                  const Icon = v.icon;
                  return (
                    <div key={r.sender_id} className="px-3 py-2" data-testid={`result-row-${r.sender_id}`}>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] ${v.cls}`}>
                          <Icon size={12} weight="bold" /> {v.label}
                        </span>
                        <span className="font-mono text-[12px] truncate flex-1">{r.sender_email}</span>
                        <span className="text-[11px] text-[#71717A]">{r.tries_used} tr{r.tries_used === 1 ? "y" : "ies"}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(r.attempts || []).map((a, i) => (
                          <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 border border-[#242424] text-[#8E8E93]" title={a.error || ""}>
                            #{a.try}→{a.recipient_email?.split("@")[0]}: {a.result}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {results.length === 0 && <div className="text-xs text-[#71717A] p-3">Running… results appear as each mailbox finishes.</div>}
                {results.length > 0 && filteredResults.length === 0 && <div className="text-xs text-[#71717A] p-3">No mailboxes in this phase.</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ v, n }) {
  const cfg = VERDICT[v];
  return <span className={`inline-flex items-center px-2 py-0.5 border ${cfg.cls}`}>{cfg.label}: {n}</span>;
}
